import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * FestivalProjectsModal · gestiona TODOS los proyectos del estudio
 * ligados a un festival concreto. Un festival puede llevarse con uno o
 * varios proyectos y cada relación (festival × proyecto) tiene su propio
 * estado + flags + notas.
 *
 * Props:
 *   open        bool
 *   festival    objeto festival (con project_statuses opcional)
 *   projects    array de proyectos del estudio (id, title)
 *   onClose     () => void
 *   onSaved     (updatedFestival) => void — devuelve festival con project_statuses recientes
 */

const STATUS_OPTIONS = [
  { key: "", label: "— sin estado —" },
  { key: "interested", label: "Me interesa" },
  { key: "submitted", label: "Enviado" },
  { key: "accepted", label: "Aceptado" },
  { key: "rejected", label: "Rechazado" },
  { key: "discarded", label: "Descartado" },
];

const STATUS_STYLE = {
  interested: "bg-blue-100 text-blue-800 ring-blue-300",
  submitted: "bg-amber-100 text-amber-900 ring-amber-300",
  accepted: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  rejected: "bg-rose-100 text-rose-800 ring-rose-300",
  discarded: "bg-stone-200 text-stone-700 ring-stone-300",
};

export default function FestivalProjectsModal({ open, festival, projects, onClose, onSaved }) {
  // Estado local por (project_id) → { status, submitted, attending, notes, _linked }
  // _linked === true significa que este proyecto ya tiene fila en la BD.
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState({});
  const [adding, setAdding] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelection, setPickerSelection] = useState({});

  // Estado GENERAL del festival (nivel estudio, sin proyecto). Cubre el
  // caso "vamos a MedIMED como oyentes sin llevar peli".
  const [studio, setStudio] = useState({
    interested: false, attending: false, discarded: false, notes: "",
  });
  const [studioSaving, setStudioSaving] = useState(false);

  const linkedIds = useMemo(
    () => new Set(Object.keys(rows).filter(pid => rows[pid]?._linked)),
    [rows]
  );
  const unlinkedProjects = useMemo(
    () => (projects || []).filter(p => !linkedIds.has(p.id)),
    [projects, linkedIds]
  );

  useEffect(() => {
    if (open && festival) {
      const map = {};
      for (const s of (festival.project_statuses || [])) {
        map[s.project_id] = {
          status: s.status || "",
          submitted: !!s.submitted,
          attending: !!s.attending,
          notes: s.notes || "",
          _linked: true,
        };
      }
      setRows(map);
      setStudio({
        interested: !!festival.studio_interested,
        attending: !!festival.studio_attending,
        discarded: !!festival.studio_discarded,
        notes: festival.studio_notes || "",
      });
      setPickerOpen(false);
      setPickerSelection({});
    }
  }, [open, festival]);

  if (!open || !festival) return null;

  const projectTitle = (pid) => projects?.find(p => p.id === pid)?.title || pid;

  // Guardar estado general del festival (nivel estudio). Se persiste al
  // vuelo cada vez que cambia un checkbox; las notas al perder foco.
  const persistStudio = async (patch) => {
    const next = { ...studio, ...patch };
    setStudio(next);
    setStudioSaving(true);
    try {
      await api.patch(`/festivals/${festival.id}/studio-state`, patch);
    } catch (e) {
      console.error("save studio state error", e);
    } finally {
      setStudioSaving(false);
    }
  };

  // Guardado autónomo por proyecto — cada cambio se persiste al momento.
  const persist = async (pid, patch, opts = {}) => {
    const current = rows[pid] || { status: "", submitted: false, attending: false, notes: "", _linked: false };
    const next = { ...current, ...patch };
    setRows(m => ({ ...m, [pid]: next }));
    setSaving(s => ({ ...s, [pid]: true }));
    try {
      const body = {};
      if (patch.status !== undefined) body.status = patch.status || null;
      if (patch.submitted !== undefined) body.submitted = patch.submitted;
      if (patch.attending !== undefined) body.attending = patch.attending;
      if (patch.notes !== undefined) body.notes = patch.notes;
      if (opts.remove) body.clear = true;

      const r = await api.put(
        `/projects/${pid}/festivals/${festival.id}/status`,
        body
      );
      if (r.data?.cleared || opts.remove) {
        setRows(m => {
          const next = { ...m };
          delete next[pid];
          return next;
        });
      } else {
        setRows(m => ({ ...m, [pid]: { ...next, _linked: true } }));
      }
    } catch (e) {
      console.error("save project link error", e);
    } finally {
      setSaving(s => ({ ...s, [pid]: false }));
    }
  };

  // Añadir uno o varios proyectos al festival (crea fila con estado
  // "interested" por defecto para que aparezca marcado).
  const addSelectedProjects = async () => {
    const chosen = Object.keys(pickerSelection).filter(k => pickerSelection[k]);
    if (chosen.length === 0) return;
    setAdding(true);
    try {
      await Promise.all(chosen.map(pid => api.put(
        `/projects/${pid}/festivals/${festival.id}/status`,
        { status: "interested" }
      )));
      const newRows = { ...rows };
      for (const pid of chosen) {
        newRows[pid] = { status: "interested", submitted: false, attending: false, notes: "", _linked: true };
      }
      setRows(newRows);
      setPickerOpen(false);
      setPickerSelection({});
    } finally {
      setAdding(false);
    }
  };

  const closeAndSync = () => {
    // Devolver un festival actualizado con project_statuses y studio_*
    // reflejando el estado interno del modal para que el padre pinte los chips.
    const project_statuses = Object.entries(rows).map(([project_id, r]) => ({
      project_id,
      festival_id: festival.id,
      status: r.status || null,
      submitted: r.submitted,
      attending: r.attending,
      notes: r.notes,
    }));
    onSaved?.({
      ...festival,
      project_statuses,
      studio_interested: studio.interested,
      studio_attending: studio.attending,
      studio_discarded: studio.discarded,
      studio_notes: studio.notes,
    });
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" data-testid="festival-projects-modal">
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-stone-200 bg-gradient-to-br from-[#941620] to-[#7a1119] px-6 py-4 text-white">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/70">Seguimiento del festival</p>
            <h2 className="mt-0.5 font-serif text-2xl leading-tight">{festival.name}</h2>
            <p className="mt-0.5 text-[11px] text-white/80">
              Primero marca el estado general (si te interesa, si vais). Luego, opcionalmente, añade los proyectos que llevas y su pipeline.
            </p>
          </div>
          <button onClick={closeAndSync}
                  data-testid="festival-projects-close"
                  className="rounded-full bg-white/20 px-2.5 py-0.5 text-lg text-white hover:bg-white/30">
            ×
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          {/* ============== ESTADO GENERAL DEL FESTIVAL (Grabarte) ============== */}
          <section className="mb-5 rounded-xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">
                Estado general del festival · Grabarte
              </h3>
              {studioSaving && <span className="text-[10px] text-stone-400">Guardando…</span>}
            </div>
            <p className="mb-3 text-[11px] text-stone-500">
              Marca aquí lo que aplique al festival en general, sin depender de proyecto (p. ej. "vamos como oyentes sin peli").
            </p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 hover:bg-blue-50">
                <input type="checkbox"
                       checked={studio.interested}
                       onChange={(e) => persistStudio({ interested: e.target.checked })}
                       data-testid="studio-interested"
                       className="h-4 w-4 accent-blue-600" />
                <span className="text-sm text-stone-800">⭐ Me interesa</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 hover:bg-emerald-50">
                <input type="checkbox"
                       checked={studio.attending}
                       onChange={(e) => persistStudio({ attending: e.target.checked })}
                       data-testid="studio-attending"
                       className="h-4 w-4 accent-emerald-600" />
                <span className="text-sm text-stone-800">🎟️ Vamos (oyente)</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 hover:bg-stone-100">
                <input type="checkbox"
                       checked={studio.discarded}
                       onChange={(e) => persistStudio({ discarded: e.target.checked })}
                       data-testid="studio-discarded"
                       className="h-4 w-4 accent-stone-500" />
                <span className="text-sm text-stone-800">✖ Descartado</span>
              </label>
            </div>
            <div className="mt-3">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-stone-500">
                📝 Notas generales del festival
              </label>
              <textarea
                value={studio.notes}
                onChange={(e) => setStudio(s => ({ ...s, notes: e.target.value }))}
                onBlur={(e) => persistStudio({ notes: e.target.value })}
                rows={2}
                data-testid="studio-notes"
                placeholder="Contacto general, valoraciones del año pasado, presupuesto de asistencia…"
                className="w-full resize-y rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-gb-red"
              />
            </div>
          </section>

          {/* ============== PROYECTOS VINCULADOS (opcional) ============== */}
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            Proyectos que llevamos (opcional)
          </h3>
          {Object.keys(rows).length === 0 && !pickerOpen && (
            <div className="mb-4 rounded-lg border border-dashed border-stone-300 bg-stone-50 p-6 text-center">
              <p className="text-sm text-stone-600">Aún no has ligado ningún proyecto a este festival.</p>
              <p className="mt-1 text-[11px] text-stone-500">Pulsa "Añadir proyecto" para empezar.</p>
            </div>
          )}

          {/* Filas por proyecto ligado */}
          <div className="space-y-3">
            {Object.keys(rows).map(pid => {
              const r = rows[pid];
              return (
                <div key={pid}
                     data-testid={`project-row-${pid}`}
                     className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-serif text-lg text-stone-900">{projectTitle(pid)}</h3>
                      {saving[pid] && <p className="text-[10px] text-stone-400">Guardando…</p>}
                    </div>
                    <button onClick={() => persist(pid, {}, { remove: true })}
                            data-testid={`project-remove-${pid}`}
                            title="Quitar este proyecto del festival"
                            className="rounded-full border border-stone-300 bg-white px-2 py-1 text-[11px] font-semibold text-stone-600 hover:border-rose-500 hover:text-rose-600">
                      ✕ Quitar
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-stone-500">Estado</label>
                      <select value={r.status || ""}
                              onChange={(e) => persist(pid, { status: e.target.value })}
                              data-testid={`project-status-${pid}`}
                              className={`w-full rounded-full px-3 py-1.5 text-[12px] font-semibold uppercase tracking-wider ring-1 ${r.status ? STATUS_STYLE[r.status] : "bg-stone-50 text-stone-500 ring-stone-200"}`}>
                        {STATUS_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 hover:bg-amber-50">
                      <input type="checkbox"
                             checked={r.submitted}
                             onChange={(e) => persist(pid, { submitted: e.target.checked })}
                             data-testid={`project-submitted-${pid}`}
                             className="h-4 w-4 accent-[#941620]" />
                      <span className="text-sm text-stone-800">✉️ Inscritos</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 hover:bg-emerald-50">
                      <input type="checkbox"
                             checked={r.attending}
                             onChange={(e) => persist(pid, { attending: e.target.checked })}
                             data-testid={`project-attending-${pid}`}
                             className="h-4 w-4 accent-emerald-600" />
                      <span className="text-sm text-stone-800">🎟️ Asistimos</span>
                    </label>
                  </div>

                  <div className="mt-3">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-stone-500">📝 Notas de este proyecto</label>
                    <textarea
                      value={r.notes}
                      onChange={(e) => setRows(m => ({ ...m, [pid]: { ...m[pid], notes: e.target.value } }))}
                      onBlur={(e) => persist(pid, { notes: e.target.value })}
                      rows={2}
                      data-testid={`project-notes-${pid}`}
                      placeholder="Contacto, tarifas, comentarios post-visita…"
                      className="w-full resize-y rounded-md border border-stone-300 px-3 py-2 text-sm outline-none focus:border-gb-red"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selector para añadir proyectos */}
          <div className="mt-4">
            {!pickerOpen ? (
              <button onClick={() => setPickerOpen(true)}
                      disabled={unlinkedProjects.length === 0}
                      data-testid="festival-projects-add-open"
                      className="w-full rounded-lg border border-dashed border-[#941620]/50 bg-[#941620]/5 px-4 py-3 text-sm font-semibold text-[#941620] hover:bg-[#941620]/10 disabled:opacity-40 disabled:cursor-not-allowed">
                {unlinkedProjects.length === 0
                  ? "Todos los proyectos ya están ligados"
                  : `+ Añadir proyecto (${unlinkedProjects.length} disponible${unlinkedProjects.length === 1 ? "" : "s"})`}
              </button>
            ) : (
              <div className="rounded-xl border border-[#941620]/30 bg-[#941620]/5 p-4">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-[#941620]">Elige uno o varios proyectos</p>
                <div className="mb-3 space-y-1.5">
                  {unlinkedProjects.map(p => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded-md bg-white px-3 py-2 text-sm hover:bg-amber-50">
                      <input type="checkbox"
                             checked={!!pickerSelection[p.id]}
                             onChange={(e) => setPickerSelection(s => ({ ...s, [p.id]: e.target.checked }))}
                             data-testid={`picker-project-${p.id}`}
                             className="h-4 w-4 accent-[#941620]" />
                      <span className="text-stone-800">{p.title}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => { setPickerOpen(false); setPickerSelection({}); }}
                          data-testid="festival-projects-add-cancel"
                          className="rounded-full border border-stone-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:border-stone-500">
                    Cancelar
                  </button>
                  <button onClick={addSelectedProjects}
                          disabled={adding || Object.values(pickerSelection).every(v => !v)}
                          data-testid="festival-projects-add-confirm"
                          className="rounded-full bg-[#941620] px-4 py-1 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#7a1119] disabled:opacity-50">
                    {adding ? "Añadiendo…" : "Añadir"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 bg-stone-50 px-6 py-3">
          <button onClick={closeAndSync}
                  data-testid="festival-projects-done"
                  className="rounded-full bg-[#941620] px-5 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#7a1119]">
            Hecho
          </button>
        </div>
      </div>
    </div>
  );
}
